import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { ChartComponent, SbChartModel } from '@ngx-seatback/charts';
import {
  API,
  FramedReportingDTO,
  LocationDTO,
  LocationFullDTO,
  LocationStatusDTO,
  PlayerCullDTO,
  ScreenScope,
} from '../../../../services/api/api';
import { UtilService } from '../../../../services/util.service';
import { ErrorService } from '../../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { SbDateTime } from '../../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { MsOptions } from '../../../../services/global/global-interfaces';
import { PermissionService } from '../../../../services/permission.service';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-page-location-reporting',
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss'],
  standalone: false,
})
export class LocationComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);

  @ViewChild('barChart') barChart: ChartComponent;
  protected readonly ScreenScope = ScreenScope;

  _from?: SbDateTime;
  _till?: SbDateTime;
  loading: boolean;
  locationChart: SbChartModel;
  interval: number;
  timeFrameData;
  filteredPlayerCulls: PlayerCullDTO[];
  playerCulls: PlayerCullDTO[];
  locations: LocationDTO[];
  filteredLocations: LocationDTO[];

  msLocationOptions: MsOptions;
  msLocationPlayerCullOptions: MsOptions;

  constructor(
    public util: UtilService,
    private api: API,
    public errorHandler: ErrorService,
    public translate: TranslateService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.loading = true;
    this.interval = 86400;
    this.filteredPlayerCulls = [];
    this.filteredPlayerCulls = [];
    this.playerCulls = [];
    this.locations = [];
    this.filteredLocations = [];
  }

  get from(): SbDateTime {
    return this._from;
  }

  set from(val) {
    this._from = val;
  }

  get till(): SbDateTime {
    return this._till;
  }

  set till(val) {
    this._till = val;
  }

  ngOnInit(): void {
    if (this.permissionService.hasPermission(this.permissionService.Permission.ss_nm_reporting)) {
      this.from = new SbDateTime(DateTime.local().startOf('day').minus({ months: 1 }));
      this.till = new SbDateTime(DateTime.local().startOf('day').minus({ day: 1 }));
      this.getTimeFrameData();

      this.initBarChart();
    }
  }

  initBarChart() {
    this.api
      .ss_nm_Network_locationStatus({
        playerCulls: this.util.arrayToIdArray(this.filteredPlayerCulls),
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (data: LocationStatusDTO) => {
          this.loadLocationChart(data);
          if (this.barChart) {
            this.barChart.reloadData(this.locationChart.data);
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  setInterval(interval) {
    this.interval = interval;
    this.getTimeFrameData();
  }

  setTimeFrame(dates?) {
    if (dates?.toDate && dates?.fromDate) {
      this.from = dates.fromDate;
      this.till = dates.toDate;
      this.getTimeFrameData();
    }
  }

  getTimeFrameData() {
    if (!this.from && !this.till) {
      this.from = new SbDateTime(DateTime.utc().startOf('day').minus({ day: 8 }));
      this.till = new SbDateTime(DateTime.utc().startOf('day').minus({ day: 1 }));
    } else {
      this.from = new SbDateTime(this.from.value.toUTC().startOf('day'));
      this.till = new SbDateTime(this.till.value.toUTC().startOf('day'));
    }
    this.api
      .dm_visitor_VisitorTrackerReporting_trackedVisitorFramed({
        playerCull: this.util.arrayToIdArray(this.filteredPlayerCulls),
        locations: this.util.arrayToIdArray(this.filteredLocations),
        from: this.from,
        till: this.till,
        productive: true,
        interval: this.interval,
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (reportingData: FramedReportingDTO) => {
          this.timeFrameData = reportingData.buckets[0].frames;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  loadLocationChart(data: LocationStatusDTO) {
    this.loading = true;
    this.locationChart = {
      title: 'Location Status',
      height: 500,
      width: 100,
      valueAssignments: [
        {
          value: 'value',
          categories: ['category'],
          valueName: 'Count',
        },
      ],
      labelBullet: true,
      data: [
        {
          category: this.translate.instant('lang.globals.total'),
          value: data.onlineStatus.total,
          color: '#016AA2',
        },
        {
          category: this.translate.instant('lang.globals.online'),
          value: data.onlineStatus.quotaAllOnline,
          color: '#2CC361',
        },
        {
          category: this.translate.instant('lang.globals.mixed'),
          value:
            data.onlineStatus.total - data.onlineStatus.quotaUnknown - data.onlineStatus.quotaAllOffline - data.onlineStatus.quotaAllOnline,
          color: '#FFC800',
        },
        {
          category: this.translate.instant('lang.globals.offline'),
          value: data.onlineStatus.quotaAllOffline,
          color: '#FF1414',
        },
        {
          category: this.translate.instant('lang.globals.unknown'),
          value: data.onlineStatus.quotaUnknown,
          color: '#999dae',
        },
      ],
    };
    this.loading = false;
  }

  update($event: any[], type: string) {
    switch (type) {
      case 'LOCATIONS':
        if ($event?.length > 0) {
          this.filteredLocations = $event;
        } else {
          this.filteredLocations = [];
        }
        break;
      case 'PLAYER_CULLS':
        if ($event?.length > 0) {
          this.filteredPlayerCulls = $event;
        } else {
          this.filteredPlayerCulls = [];
        }
        this.initBarChart();
        break;
    }
    this.getTimeFrameData();
  }

  msRequest(type: string, options?: MsOptions) {
    switch (type) {
      case 'LOCATIONS':
        if (options) {
          this.msLocationOptions = options;
        }
        if (this.msLocationOptions?.pagination) {
          this.api
            .ss_fm_Location_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msLocationOptions?.search),
              },
              this.msLocationOptions?.pagination
            )
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: (locs: LocationFullDTO[]) => {
                this.locations = locs;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
      case 'PLAYER_CULLS':
        if (options) {
          this.msLocationPlayerCullOptions = options;
        }
        if (this.msLocationPlayerCullOptions?.pagination) {
          this.api
            .cs_Cull_retrieveAll(
              {
                type: 'PlayerCull',
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msLocationPlayerCullOptions?.search),
              },
              this.msLocationPlayerCullOptions?.pagination
            )
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: (culls: PlayerCullDTO[]) => {
                this.playerCulls = culls;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
    }
  }
}
